// Engineer sign-in (PWA): email, then a 6-digit code emailed to it. No password (Session BCE, Director decision 12
// October 2026). Separate from the office Google sign-in (/login). On success the session is set and we go to the
// engineer home.
//
// The two steps and the Resend clock follow the Client Portal's (portal/CodeSignIn.tsx): the Resend button's cooldown
// re-reads the clock rather than counting ticks, and re-reads it when the page comes back into view, because this is
// exactly the screen a person leaves to fetch the code out of their email (the CLOCK THAT GATES A CONTROL rule,
// Session AQF). The send reply never says whether an address is registered, so neither does this page.
//
// Session BCK: engineers of ONE subcontractor may share an email address. When the verified address is shared the server
// signs nobody in yet and answers a short "Who are you?" step: first names only, large buttons, a one-time token that
// lives only in this component's state (never stored, never in the URL). Choosing a name finishes the sign-in. A person
// on an address of their own never sees it.
//
// The email is pre-filled from ?email= (the app details email can link here). It does NOT jump to the code step: the
// link carries no code, and the person has to ask for one.
//
// Session ASB: when the server ended the session (ProtectedRoute adds ?reason=), the engineer is told plainly, and
// told that anything queued on the phone is safe: the outboxes and the job sheet drafts are never cleared by a
// sign-out, and they send once the engineer is signed in again (EngineerDataContext drains on mount).
//
// Session ASB review, finding F2: this page is ALSO where an admin or office user driving the engineer app lands when
// their session ends (the Director works the PWA on his admin login, which idles out after 60 minutes). Those accounts
// have no emailed code: they use Google (GOOGLE_SIGN_IN_TO_ENGINEER_URL, returning to the engineer app so queued work
// drains) or, secondary, their password. When the session that just ended was an admin's or an office user's, Google
// is offered first, above the form.
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { ApiError, GOOGLE_SIGN_IN_TO_ENGINEER_URL, api } from "../../lib/api";
import type { EngineerSelectionStep } from "../../lib/api";

const SESSION_ENDED_ENGINEER_MESSAGE =
  "Your session has ended. Sign in again. Anything you had not sent yet is still saved on this phone and will send once you are signed in.";
const SESSION_ENDED_GOOGLE_MESSAGE =
  "Your session has ended. You sign in with Google, so use the button below. Anything you had not sent yet is still saved on this phone and will send once you are signed in.";

const RESEND_COOLDOWN_MS = 60 * 1000;
const OFFICE_NUMBER = "0203 576 4760";

const fieldCls = "w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 outline-none focus:border-ele-green";
const noticeCls = "mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong";

function serverSentence(err: unknown): string | null {
  if (err instanceof ApiError && typeof err.body?.message === "string" && err.body.message) return err.body.message;
  return null;
}

export function EngineerLogin() {
  const { engineerLogin, engineerCodeSignIn, engineerSelectSignIn, endedSessionRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Session PN1: where the engineer was heading when sign-in was asked for (a tapped notification opens /engineer/jobs). Only an
  // engineer-app page is honoured; anything else, and the sign-in page itself, lands on the home screen as before.
  const from = (location.state as { from?: unknown } | null)?.from;
  const afterSignIn = typeof from === "string" && from.startsWith("/engineer") && !from.startsWith("/engineer/login") ? from : "/engineer";
  const [params] = useSearchParams();
  const sessionEnded = params.get("reason") !== null;
  // The session that just ended was an admin's or an office user's: they sign in with Google, not with a code.
  const googleFirst = sessionEnded && (endedSessionRole === "admin" || endedSessionRole === "office");

  const [step, setStep] = useState<"email" | "code" | "who">("email");
  const [selection, setSelection] = useState<EngineerSelectionStep | null>(null);
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState(false);
  const [resendAt, setResendAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Staff password sign-in (secondary): an admin or office account has no emailed code.
  const [staffId, setStaffId] = useState("");
  const [staffPassword, setStaffPassword] = useState("");
  const [staffError, setStaffError] = useState<string | null>(null);
  const [staffBusy, setStaffBusy] = useState(false);

  // Arm the cooldown from ONE clock read (Session AQF), never from a value read at mount.
  function armResendCooldown() {
    const t = Date.now();
    setNow(t);
    setResendAt(t + RESEND_COOLDOWN_MS);
  }

  // Tick only to re-render the countdown. It re-reads the clock (never counts ticks), re-reads it again when the page
  // comes back into view, and stops once the cooldown is over.
  useEffect(() => {
    if (resendAt == null) return;
    const sync = () => setNow(Date.now());
    const id = setInterval(() => {
      sync();
      if (Date.now() >= resendAt) clearInterval(id);
    }, 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") sync();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [resendAt]);

  const resendReady = resendAt == null || now >= resendAt;
  const resendSecondsLeft = resendAt == null ? 0 : Math.max(0, Math.ceil((resendAt - now) / 1000));

  async function sendCode() {
    setBusy(true);
    setError(null);
    setConnectionError(false);
    try {
      const res = await api.engineerSignIn.requestCode(email.trim());
      if (res.ok === false) {
        setError(res.message ?? `We could not send the code right now. Please call the office on ${OFFICE_NUMBER}.`);
      } else {
        setStep("code");
        setCode("");
        armResendCooldown();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(serverSentence(err) ?? `We could not send the code right now. Please call the office on ${OFFICE_NUMBER}.`);
      } else {
        setConnectionError(true);
      }
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitEmail(e: FormEvent) {
    e.preventDefault();
    await sendCode();
  }

  async function onSubmitCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setConnectionError(false);
    setBusy(true);
    try {
      const next = await engineerCodeSignIn(email.trim(), code.trim());
      if (next) {
        setSelection(next);
        setStep("who");
        return;
      }
      navigate(afterSignIn, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          serverSentence(err) ??
            (err.status === 423
              ? `Too many wrong codes. Please wait a few minutes, or call the office on ${OFFICE_NUMBER}.`
              : "That code did not match, or it has expired. Check the code and try again, or ask for a new one."),
        );
      } else {
        setConnectionError(true);
      }
    } finally {
      setBusy(false);
    }
  }

  async function pickEngineer(engineerId: string) {
    if (!selection || busy) return;
    setError(null);
    setConnectionError(false);
    setBusy(true);
    try {
      await engineerSelectSignIn(email.trim(), selection.token, engineerId);
      navigate(afterSignIn, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // The step is single use: whatever went wrong, the way forward is a fresh code.
        setSelection(null);
        setStep("email");
        setCode("");
        setError(serverSentence(err) ?? "That step has expired. Please ask for a new code.");
      } else {
        setConnectionError(true);
      }
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (!resendReady || busy) return;
    await sendCode();
  }

  async function onStaffSubmit(e: FormEvent) {
    e.preventDefault();
    setStaffError(null);
    setStaffBusy(true);
    try {
      await engineerLogin(staffId.trim(), staffPassword);
      navigate(afterSignIn, { replace: true });
    } catch (err) {
      setStaffError(
        err instanceof ApiError && err.code === "invalid_credentials"
          ? "Those details did not match, or this account is no longer active."
          : "Could not sign in right now. Please try again.",
      );
    } finally {
      setStaffBusy(false);
    }
  }

  return (
    <div className="engineer-ui flex min-h-dvh flex-col items-center justify-center bg-ele-base px-4 text-ele-body">
      <img
        src="/ELE_logo_new_reqt_FINALv2PNG.png"
        alt="East London Elevators"
        className="mb-6 block h-auto w-auto max-w-[200px]"
      />
      <div className="w-full max-w-sm rounded-ele border border-ele-purple-accent bg-ele-surface p-8">
        <h1 className="mb-1 font-heading text-3xl tracking-wide">
          East London <span className="text-ele-green">Elevators</span>
        </h1>
        <p className="mb-6 font-menu text-xs uppercase tracking-widest text-ele-blue">Engineer sign-in</p>

        {googleFirst && (
          <div className="mb-6">
            <p role="status" aria-live="polite" className="mb-3 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {SESSION_ENDED_GOOGLE_MESSAGE}
            </p>
            <a
              href={GOOGLE_SIGN_IN_TO_ENGINEER_URL}
              className="flex min-h-11 w-full items-center justify-center rounded-ele bg-ele-green py-2 font-heading tracking-wide text-ele-bg transition"
            >
              Sign in with Google
            </a>
          </div>
        )}

        {connectionError && (
          <p role="alert" aria-live="assertive" className={noticeCls}>
            We could not reach the server. Check your signal and try again, or call the office on {OFFICE_NUMBER}.
          </p>
        )}

        {step === "who" && selection ? (
          <div>
            <p className="mb-4 text-sm">Who are you? Tap your name to sign in.</p>
            {error && (
              <p role="alert" aria-live="assertive" className={noticeCls}>
                {error}
              </p>
            )}
            <ul className="space-y-3">
              {selection.engineers.map((e) => (
                <li key={e.id}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void pickEngineer(e.id)}
                    className="min-h-14 w-full rounded-ele bg-ele-green py-3 font-heading text-xl tracking-wide text-ele-bg transition disabled:opacity-50"
                  >
                    {e.firstName}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : step === "email" ? (
          <form onSubmit={onSubmitEmail}>
            <label className="mb-1 block text-sm" htmlFor="engineer-email">
              Email address
            </label>
            <input
              id="engineer-email"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`mb-4 ${fieldCls}`}
            />
            <p className="mb-4 text-xs text-ele-body">We will email you a 6-digit code. There is no password to remember.</p>

            {error && (
              <p role="alert" aria-live="assertive" className={noticeCls}>
                {error}
              </p>
            )}
            {!error && sessionEnded && !googleFirst && (
              <p role="status" aria-live="polite" className={noticeCls}>
                {SESSION_ENDED_ENGINEER_MESSAGE}
              </p>
            )}

            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="min-h-11 w-full rounded-ele bg-ele-green py-2 font-heading tracking-wide text-ele-bg transition disabled:opacity-50"
            >
              {busy ? "Sending..." : "Email me a code"}
            </button>
          </form>
        ) : (
          <form onSubmit={onSubmitCode}>
            <p className="mb-4 text-sm">
              If <strong className="text-ele-strong">{email.trim()}</strong> is set up for the app, we have emailed it a 6-digit code.
              Type it below.
            </p>
            <label className="mb-1 block text-sm" htmlFor="engineer-code">
              6-digit code
            </label>
            <input
              id="engineer-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="000000"
              className={`mb-4 text-center text-2xl tracking-[0.4em] ${fieldCls}`}
            />

            {error && (
              <p role="alert" aria-live="assertive" className={noticeCls}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy || code.length !== 6}
              className="min-h-11 w-full rounded-ele bg-ele-green py-2 font-heading tracking-wide text-ele-bg transition disabled:opacity-50"
            >
              {busy ? "Checking..." : "Sign in"}
            </button>
            <div className="mt-4 flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  setCode("");
                  setError(null);
                }}
                className="text-ele-body underline hover:text-ele-green"
              >
                Change email
              </button>
              <button
                type="button"
                disabled={!resendReady || busy}
                onClick={() => void resend()}
                className="text-ele-green underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
              >
                {resendReady ? "Send a new code" : `Send a new code (${resendSecondsLeft}s)`}
              </button>
            </div>
          </form>
        )}

        {/* Session AFW: a poor-signal engineer who lands here mid-job can still read their saved job
            list and site access details without signing in (they cannot record work until they do). */}
        <Link
          to="/engineer/offline"
          className="mt-4 flex min-h-11 w-full items-center justify-center rounded-ele border border-ele-field-border py-2 text-center font-heading text-sm tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
        >
          Continue offline
        </Link>

        <div className="mt-6 border-t border-ele-purple-accent pt-4 text-center text-xs text-ele-body">
          <p>
            Office staff?{" "}
            <a href={GOOGLE_SIGN_IN_TO_ENGINEER_URL} className="text-ele-green hover:underline">
              Sign in with Google
            </a>
            .
          </p>
          <details className="mt-3 text-left">
            <summary className="cursor-pointer text-center text-ele-body hover:text-ele-green">
              Office staff: sign in with a password
            </summary>
            <form onSubmit={onStaffSubmit} className="mt-3">
              <label className="mb-1 block text-sm" htmlFor="staff-id">
                Email or username
              </label>
              <input
                id="staff-id"
                autoComplete="username"
                autoCapitalize="none"
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                className={`mb-3 ${fieldCls}`}
              />
              <label className="mb-1 block text-sm" htmlFor="staff-password">
                Password
              </label>
              <input
                id="staff-password"
                type="password"
                autoComplete="current-password"
                value={staffPassword}
                onChange={(e) => setStaffPassword(e.target.value)}
                className={`mb-3 ${fieldCls}`}
              />
              {staffError && (
                <p role="alert" aria-live="assertive" className={noticeCls}>
                  {staffError}
                </p>
              )}
              <button
                type="submit"
                disabled={staffBusy || !staffId.trim() || !staffPassword}
                className="min-h-11 w-full rounded-ele border border-ele-field-border py-2 font-heading tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-50"
              >
                {staffBusy ? "Signing in..." : "Sign in with password"}
              </button>
            </form>
          </details>
        </div>
        <p className="mt-4 text-center font-menu text-xs text-ele-body">Need help? Call the office on {OFFICE_NUMBER}.</p>
      </div>
    </div>
  );
}
